/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { isTestMatch } from "../../helper/helper";

export { isTestMatch };

/** One call that feeds the whole Home tab (featured matches, stats, top performers, teams, top of the table). */
export const useHomeSummary = (tid?: string | null) =>
  useQuery({
    queryKey: ["thSummary", tid],
    enabled: !!tid,
    staleTime: 30_000,
    queryFn: async () => (await Api.get(`tournament/${tid}/home-summary`)).data as any,
  });

/** The tournament record: organiser, dates, format, ball, points rules. */
export const useTournamentInfo = (tid?: string | null) =>
  useQuery({
    queryKey: ["thInfo", tid],
    enabled: !!tid,
    staleTime: 60_000,
    queryFn: async () => (await Api.get(`${QUERY_KEY.addTournament}/${tid}`)).data as any,
  });

export type Sponsor = { id: string; name: string; image?: string; role?: string };
export const useSponsors = (tid?: string | null) =>
  useQuery({
    queryKey: ["thSponsors", tid],
    enabled: !!tid,
    staleTime: 60_000,
    queryFn: async (): Promise<Sponsor[]> => {
      const raw = (await Api.get(`getAllSponsorsForATournament/${tid}`)).data;
      const list: any[] = Array.isArray(raw) ? raw : (raw?.data ?? raw?.sponsors ?? []);
      return list
        .map((s, i) => ({ id: String(s.sponsorId ?? s.id ?? i), name: String(s.sponsorName ?? s.name ?? "").trim(), image: s.imageUrl ?? s.logoUrl ?? s.imageUri ?? s.logo ?? undefined, role: s.sponsorType ?? s.role ?? s.title ?? undefined }))
        .filter((s) => s.name);
    },
  });

export type PtRow = {
  teamSnapshotId: string; name: string; divisionId: string; rank: number;
  m: number; w: number; l: number; t: number; d: number; nr: number; pts: number; bonus: number; nrr: number | null;
  forRuns: number; forBalls: number; againstRuns: number; againstBalls: number; form: string[];
};

const toRow = (r: any): PtRow => ({
  teamSnapshotId: String(r.teamSnapshotId ?? ""), name: String(r.teamSnapshotName ?? "").trim(), divisionId: String(r.divisionId ?? ""), rank: r.rank ?? 0,
  m: r.matches ?? 0, w: r.won ?? 0, l: r.lost ?? 0, t: r.tie ?? 0, d: r.draw ?? 0, nr: r.noResult ?? 0, pts: r.points ?? 0, bonus: r.bonusPoints ?? 0,
  nrr: typeof r.netRunrate === "number" ? r.netRunrate : null,
  forRuns: r.forRuns ?? 0, forBalls: r.forBalls ?? 0, againstRuns: r.againstRuns ?? 0, againstBalls: r.againstBalls ?? 0,
  form: Array.isArray(r.lastMatchesResult) ? r.lastMatchesResult.map((x: any) => String(x).toUpperCase()) : [],
});

/** Points tables of a division, one list per group (keyed by the group's division id). */
export const useGroupTables = (divisionId?: string | null) =>
  useQuery({
    queryKey: ["ptGroups", divisionId],
    enabled: !!divisionId,
    staleTime: 30_000,
    queryFn: async () => {
      const map = ((await Api.get(`${QUERY_KEY.getCombinedPointsTableByDivision}/${divisionId}`)).data ?? {}) as Record<string, any[]>;
      const out: Record<string, PtRow[]> = {};
      Object.entries(map).forEach(([k, rows]) => { out[k] = (Array.isArray(rows) ? rows : []).map(toRow); });
      return out;
    },
  });

export const fmtDay = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
export const fmtLong = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
export const nice = (s?: string | null) => (s ?? "").toString().trim().toLowerCase().split(/[_\s]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
export const nrrText = (v: number | null) => (v == null ? "–" : `${v > 0 ? "+" : ""}${v.toFixed(3)}`);
/** Multi-day formats (Test / "Multi Day", or matches scheduled over several days) rank by quotient, not net run rate. */
export const isMultiDay = (info?: { matchType?: string | null; scheduledDays?: number | null } | null) => isTestMatch(info?.matchType) || (info?.scheduledDays ?? 0) > 1;
/** A quotient is a plain ratio: no sign, unlike NRR. */
export const quotientText = (v: number | null) => (v == null ? "–" : v.toFixed(3));
export const overs = (balls: number) => `${Math.floor(balls / 6)}.${balls % 6}`;
